import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, Edit2, Trash2, ArrowRight, Activity, Clock, Shield, Search } from 'lucide-react';
import Swal from 'sweetalert2';

import {
    fetchSeuilPassageRequest,
    createSeuilPassageRequest,
    updateSeuilPassageRequest,
    deleteSeuilPassageRequest
} from '../../../redux/actions/seuilPassageActions';
import { getAllJobsRequest } from '../../../redux/actions/Jobactions';

import './SeuilPassageManagement.css';
// En supposant que crTheme a déjà des variables globales
// Sinon nous utilisons nos propres classes

/* ════════════════════════════════════════
   Modal Simplifiée
════════════════════════════════════════ */
const Modal = ({ open, title, onClose, children, footer }) => {
    if (!open) return null;
    return (
        <div className="seuil-modal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="seuil-modal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="seuil-modal__header">
                    <div className="seuil-modal__title">{title}</div>
                    <button className="seuil-icon-btn" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="seuil-modal__body">{children}</div>
                {footer && <div className="seuil-modal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ════════════════════════════════════════
   Composant Principal
════════════════════════════════════════ */
const SeuilPassageManagement = () => {
    const dispatch = useDispatch();
    const { list: seuils, loading } = useSelector((state) => state.seuilPassage || { list: [], loading: false });
    const { jobs } = useSelector((state) => state.jobs || { jobs: [] });
    const currentUser = useSelector((state) => state.auth?.user);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formState, setFormState] = useState({
        posteOrigineId: '',
        posteCibleId: '',
        seuilGlobalMatch: 80,
        anneesExperienceRequises: 0,
        validationManagerRequise: true
    });

    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (currentUser?.companyId) {
            dispatch(fetchSeuilPassageRequest(currentUser.companyId));
            dispatch(getAllJobsRequest(currentUser.companyId));
        }
    }, [dispatch, currentUser?.companyId]);

    const activeJobs = useMemo(() => Array.isArray(jobs) ? jobs.filter(j => j.isActive) : [], [jobs]);

    const availableTargetJobs = useMemo(() => {
        return activeJobs.filter(j => j.id.toString() !== formState.posteOrigineId.toString());
    }, [activeJobs, formState.posteOrigineId]);

    const filteredSeuils = useMemo(() => {
        if (!searchTerm) return seuils;
        const low = searchTerm.toLowerCase();
        return seuils.filter(s => 
            s.posteOrigineName?.toLowerCase().includes(low) || 
            s.posteCibleName?.toLowerCase().includes(low)
        );
    }, [seuils, searchTerm]);

    const openCreateModal = () => {
        setEditingId(null);
        setFormState({
            posteOrigineId: '',
            posteCibleId: '',
            seuilGlobalMatch: 80,
            anneesExperienceRequises: 0,
            validationManagerRequise: true
        });
        setIsModalOpen(true);
    };

    const openEditModal = (seuil) => {
        setEditingId(seuil.id);
        setFormState({
            posteOrigineId: seuil.posteOrigineId,
            posteCibleId: seuil.posteCibleId,
            seuilGlobalMatch: seuil.seuilGlobalMatch || 0,
            anneesExperienceRequises: seuil.anneesExperienceRequises || 0,
            validationManagerRequise: seuil.validationManagerRequise ?? true
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Vous êtes sur le point de supprimer ce seuil de passage. Cette action est irréversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteSeuilPassageRequest(id));
                Swal.fire('Supprimé !', 'Le seuil a été supprimé.', 'success');
            }
        });
    };

    const handleSave = () => {
        if (!formState.posteOrigineId || !formState.posteCibleId) {
            Swal.fire('Erreur', 'Veuillez sélectionner le poste d\'origine et le poste cible.', 'warning');
            return;
        }

        if (formState.posteOrigineId === formState.posteCibleId) {
            Swal.fire('Erreur', 'Le poste d\'origine doit être différent du poste cible.', 'warning');
            return;
        }

        const payload = {
            ...formState,
            companyId: currentUser.companyId,
            posteOrigineId: Number(formState.posteOrigineId),
            posteCibleId: Number(formState.posteCibleId),
        };

        if (editingId) {
            dispatch(updateSeuilPassageRequest(editingId, payload));
        } else {
            // Check doublon coté front
            const existeDeja = seuils.find(s => s.posteOrigineId === payload.posteOrigineId && s.posteCibleId === payload.posteCibleId);
            if (existeDeja) {
                Swal.fire('Doublon', 'Une règle de passage existe déjà entre ces deux postes.', 'error');
                return;
            }
            dispatch(createSeuilPassageRequest(payload));
        }
        setIsModalOpen(false);
    };

    return (
        <div className="seuil-passage-container">
            
            <div className="seuil-header">
                <div>
                    <h2> Seuils de passage & parcours</h2>
                    <p className="seuil-subtitle">Définissez les conditions d'évolution entre les différents postes de votre entreprise.</p>
                </div>
                <button className="seuil-btn seuil-btn--primary" onClick={openCreateModal}>
                    <Plus size={16}/> Ajouter une règle
                </button>
            </div>

            <div className="seuil-card">
                <div className="seuil-toolbar">
                     <div className="seuil-search">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Rechercher par poste..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="seuil-card__body">
                    <div className="seuil-table-wrap">
                        <table className="seuil-table">
                            <thead>
                                <tr>
                                    <th>Trajectoire de carrière</th>
                                    <th>Match Requis</th>
                                    <th>Ancienneté Requise</th>
                                    <th>Validation</th>
                                    <th className="seuil-actions-col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && seuils.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="seuil-empty-cell">Chargement...</td>
                                    </tr>
                                ) : filteredSeuils.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="seuil-empty-cell">
                                            <div className="seuil-empty">
                                                <Activity size={40} />
                                                <h4>Aucun seuil défini</h4>
                                                <p>Créez votre première règle de passage de poste.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSeuils.map((seuil) => (
                                        <tr key={seuil.id}>
                                            <td>
                                                <div className="seuil-path">
                                                    <span className="seuil-origin">{seuil.posteOrigineName}</span>
                                                    <ArrowRight size={14} />
                                                    <span className="seuil-target">{seuil.posteCibleName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="seuil-score-badge">
                                                    {seuil.seuilGlobalMatch}% adéquation
                                                </span>
                                            </td>
                                            <td>
                                                {seuil.anneesExperienceRequises > 0 ? (
                                                    <span className="seuil-exp-badge">
                                                        <Clock size={12}/> {seuil.anneesExperienceRequises} an{seuil.anneesExperienceRequises > 1 ? 's' : ''}
                                                    </span>
                                                ) : <span className="seuil-muted">Aucune</span>}
                                            </td>
                                            <td>
                                                {seuil.validationManagerRequise ? (
                                                    <span className="seuil-validation">
                                                        <Shield size={14}/> Manager
                                                    </span>
                                                ) : (
                                                    <span className="seuil-muted">Automatique</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="seuil-row-actions">
                                                    <button className="seuil-icon-btn seuil-icon-btn--edit" onClick={() => openEditModal(seuil)} title="Modifier">
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button className="seuil-icon-btn seuil-icon-btn--delete" onClick={() => handleDelete(seuil.id)} title="Supprimer">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Modal
                open={isModalOpen}
                title={editingId ? "Modifier le Seuil de Passage" : "Créer un Seuil de Passage"}
                onClose={() => setIsModalOpen(false)}
                footer={
                    <>
                        <button className="seuil-btn seuil-btn--ghost" onClick={() => setIsModalOpen(false)}>Annuler</button>
                        <button className="seuil-btn seuil-btn--primary" onClick={handleSave}>Enregistrer</button>
                    </>
                }
            >
                <div className="seuil-form-grid">
                    <div className="seuil-form-group">
                        <label>Poste d'origine</label>
                        <select 
                            className="seuil-select"
                            value={formState.posteOrigineId}
                            onChange={(e) => setFormState({...formState, posteOrigineId: e.target.value})}
                        >
                            <option value="">-- Sélectionner un poste --</option>
                            {activeJobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                        </select>
                    </div>

                    <div className="seuil-form-group">
                        <label>Poste cible (Visé)</label>
                        <select 
                            className="seuil-select" 
                            value={formState.posteCibleId}
                            onChange={(e) => setFormState({...formState, posteCibleId: e.target.value})}
                        >
                            <option value="">-- Sélectionner un poste --</option>
                            {availableTargetJobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                        </select>
                    </div>

                    <div className="seuil-arrow-divider">
                        <div className="seuil-arrow-line"></div>
                        <ArrowRight size={18} />
                        <div className="seuil-arrow-line"></div>
                    </div>

                    <div className="seuil-form-group">
                        <label>Score Global Requis (%)</label>
                        <input 
                            type="number" 
                            className="seuil-input" 
                            min="0" max="100"
                            placeholder="ex: 80"
                            value={formState.seuilGlobalMatch}
                            onChange={(e) => setFormState({...formState, seuilGlobalMatch: parseInt(e.target.value) || 0})}
                        />
                        <small>Pourcentage d'adéquation de compétences exigé.</small>
                    </div>

                    <div className="seuil-form-group">
                        <label>Ancienneté Requise (Années)</label>
                        <input 
                            type="number" 
                            className="seuil-input" 
                            min="0"
                            placeholder="ex: 2"
                            value={formState.anneesExperienceRequises}
                            onChange={(e) => setFormState({...formState, anneesExperienceRequises: parseInt(e.target.value) || 0})}
                        />
                        <small>Années d'expérience minimales dans le poste source.</small>
                    </div>

                    <div className="seuil-form-group seuil-form-col-full">
                        <label className="seuil-checkbox-label">
                            <input 
                                type="checkbox" 
                                checked={formState.validationManagerRequise}
                                onChange={(e) => setFormState({...formState, validationManagerRequise: e.target.checked})}
                            />
                            Nécessite la validation explicite du Manager
                        </label>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default SeuilPassageManagement;
